import { normaliseRoomName } from "./bedroomValidation";

export function buildAssignmentIndex(rooms = []) {
  const assignments = {};
  rooms.forEach((room) => {
    (room.residents || []).forEach((resident) => {
      assignments[resident.id] = room.id;
    });
  });
  return assignments;
}

export function getRoomById(rooms = [], roomId) {
  return rooms.find((room) => String(room.id) === String(roomId)) || null;
}

export function getAssignedRoom(rooms = [], residentId) {
  return rooms.find((room) =>
    room.residents?.some((resident) => resident.id === residentId)
  ) || null;
}

export function getAssignedRoomName(rooms = [], residentId) {
  return getAssignedRoom(rooms, residentId)?.groupName || "";
}

export function getAssignedRoomHouse(rooms = [], residentId) {
  return getAssignedRoom(rooms, residentId)?.house || "";
}

export function getAssignedRoomEnsuite(rooms = [], residentId) {
  return getAssignedRoom(rooms, residentId)?.ensuite ?? null;
}

export function cloneRooms(rooms = []) {
  return rooms.map((room) => ({
    ...room,
    residents: [...(room.residents || [])],
  }));
}

export function moveResidentInRooms({
  rooms = [],
  resident,
  fromRoomId = null,
  toRoomId = null,
}) {
  return rooms.map((room) => {
    const withoutResident = (room.residents || []).filter(
      (r) => r.id !== resident.id
    );

    if (toRoomId != null && String(room.id) === String(toRoomId)) {
      return { ...room, residents: [...withoutResident, resident] };
    }

    if (fromRoomId != null && String(room.id) === String(fromRoomId)) {
      return { ...room, residents: withoutResident };
    }

    if (toRoomId == null || String(room.id) !== String(toRoomId)) {
      return { ...room, residents: withoutResident };
    }

    return room;
  });
}

export function filterResidents({
  residents = [],
  selectedCampus = "",
  selectedGender = "",
  selectedAge = "",
  selectedNationality = "",
  nameSearch = "",
}) {
  const search = String(nameSearch || "").trim().toLowerCase();

  return residents.filter((resident) => {
    if (selectedCampus && resident.campus !== selectedCampus) return false;
    if (selectedGender && resident.gender !== selectedGender) return false;
    if (selectedAge && Number(resident.age) !== Number(selectedAge)) return false;
    if (selectedNationality && resident.nationality !== selectedNationality) return false;
    if (
      search &&
      !`${resident.name} ${resident.surname}`.toLowerCase().includes(search)
    ) {
      return false;
    }
    return true;
  });
}

export function filterRoomsBySearch(rooms = [], roomNameSearch = "") {
  const search = normaliseRoomName(roomNameSearch);
  if (!search) return rooms;
  return rooms.filter((room) =>
    normaliseRoomName(room.groupName).includes(search)
  );
}

export function getVisibleUnassignedResidents({
  residents = [],
  rooms = [],
  filters = {},
}) {
  const filteredResidents = filterResidents({ residents, ...filters });
  const assignments = buildAssignmentIndex(rooms);
  return filteredResidents.filter((resident) => !assignments[resident.id]);
}

export function formatBedroomDisplayName(room = {}) {
  const house = String(room.house || "").trim();
  const bedroomName = String(room.bedroomName || room.groupName || "").trim();

  const isEnsuite =
    room.ensuite === true ||
    room.ensuite === "true" ||
    room.ensuite === 1;

  const ensuiteSuffix = isEnsuite ? " (ensuite)" : "";

  if (house && bedroomName) {
    return `${house} ${bedroomName}${ensuiteSuffix}`;
  }

  if (bedroomName) {
    return `${bedroomName}${ensuiteSuffix}`;
  }

  if (house) {
    return `${house}${ensuiteSuffix}`;
  }

  return "";
}

export function getAvailableRoomNames(rooms = []) {
  return rooms
    .filter((room) => {
      const capacity = Number(room.capacity) || 0;
      return capacity > 0 && (room.residents?.length || 0) < capacity;
    })
    .map((room) => {
      const freeSpaces =
        (Number(room.capacity) || 0) - (room.residents?.length || 0);

      const displayName = formatBedroomDisplayName(room);

      return {
        value: displayName,
        label: `${displayName} (${freeSpaces} spaces)`,
        house: room.house || "",
        ensuite: room.ensuite,
        bedroomName: room.bedroomName || room.groupName || "",
        roomId: room.bedroomId || room.id,
      };
    });
}

export function buildPendingChanges({ displayRooms = [], databaseRooms = [] }) {
  const displayAssignments = buildAssignmentIndex(displayRooms);
  const databaseAssignments = buildAssignmentIndex(databaseRooms);
  const residentIds = new Set([
    ...Object.keys(displayAssignments),
    ...Object.keys(databaseAssignments),
  ]);

  return Array.from(residentIds)
    .filter(
      (residentId) =>
        String(displayAssignments[residentId] || "") !==
        String(databaseAssignments[residentId] || "")
    )
    .map((residentId) => ({
      residentId,
      fromRoomId: databaseAssignments[residentId] ?? null,
      toRoomId: displayAssignments[residentId] ?? null,
    }));
}